import {
  hasNativeAudioPlugin,
  nativeStartInputStream,
  nativeStopInputStream,
  subscribeInputFrames,
  type NativeInputFrames,
} from '@/lib/nativeAudio'

import nativeInputProcessorUrl from '@/audio/worklets/nativeInputProcessor.ts?worker&url'

/**
 * Bridge between the native `BusswerksAudio` input stream and the
 * Web Audio graph. Owns an `AudioWorkletNode` that receives PCM
 * chunks from the native side (via `inputFrames` events) and emits
 * them back into the graph — so existing bus FX / volume / pan
 * routing all apply to the monitor signal.
 *
 * Used only on Catalyst today; iOS / iPadOS + web still use the
 * direct `getUserMedia` + `MediaStreamAudioSourceNode` path in
 * `AudioInput.ts`. Gated by `hasNativeAudioPlugin()` so the import
 * is safe to include unconditionally on web (the worklet just
 * never gets instantiated there).
 *
 * Lifecycle:
 *   1. `start(deviceId)` adds the worklet module (once per
 *      AudioContext), builds a node, subscribes to `inputFrames`,
 *      and kicks off the native tap.
 *   2. `connectTo(destination)` wires the node's output to a bus
 *      input. Idempotent re-call rewires cleanly.
 *   3. `stop()` tears everything down — native stop, event
 *      unsubscribe, node disconnect. Safe to call redundantly.
 */

export type NativeInputStreamStatus = 'idle' | 'starting' | 'active' | 'error'

export interface NativeInputStreamEvents {
  onStatus?: (status: NativeInputStreamStatus) => void
  onError?: (message: string) => void
  /** Fill-level percentage [0..1] of the worklet's internal ring
   *  buffer, emitted ~every 21 ms while active. UI can surface a
   *  "bridge lagging" warning when this chronically stays < 0.1. */
  onFill?: (fill: number) => void
}

interface NodeStatusMessage {
  type: 'status'
  fill: number
  unreadFrames: number
}

/** Track which AudioContexts have already registered the worklet
 *  module — `audioWorklet.addModule` is safe to call twice but
 *  returns a promise each time, so caching saves latency on the
 *  second start. */
const moduleRegistered = new WeakSet<BaseAudioContext>()

/** Feature-detect `Uint8Array.fromBase64`. Resolved lazily so
 *  ship-time bundlers can't const-fold it before the polyfill /
 *  runtime has a chance to install. */
interface Uint8ArrayWithFromBase64 {
  fromBase64?: (s: string) => Uint8Array
}

const hasFromBase64 = (): boolean =>
  typeof (Uint8Array as unknown as Uint8ArrayWithFromBase64).fromBase64 ===
  'function'

/** Decode a base64 string into a `Uint8Array` using the fastest
 *  path the runtime supports. Order of preference:
 *    1. `Uint8Array.fromBase64` — intrinsic, ~8× faster
 *    2. `Uint8Array.from(atob, charCode)` — engine-optimised
 *    3. manual loop (shouldn't trigger on any modern target) */
function decodeBase64Bytes(data: string): Uint8Array {
  if (hasFromBase64()) {
    const fromB64 = (Uint8Array as unknown as Uint8ArrayWithFromBase64)
      .fromBase64!
    return fromB64(data)
  }
  if (typeof atob === 'function') {
    const binary = atob(data)
    return Uint8Array.from(binary, ch => ch.charCodeAt(0))
  }
  // Worst case — shouldn't happen on any platform Busswerks ships
  // to. Keeps the decoder honest against an SSR or test harness
  // without `atob`.
  return new Uint8Array(0)
}

export class NativeInputStream {
  private ctx: AudioContext
  private node: AudioWorkletNode | null = null
  private connectedTo: AudioNode | null = null
  private unsubscribe: (() => void) | null = null
  private status: NativeInputStreamStatus = 'idle'
  /** Device identifier this instance is bound to. Empty string =
   *  the OS default input. Matches the key used in the per-context
   *  registry + the `deviceId` tag the native side stamps onto
   *  every `inputFrames` event. Fixed at construction so a stream
   *  never silently jumps devices; callers swap by requesting a
   *  different instance from `ensureNativeInputStream`. */
  readonly deviceId: string
  private currentChannelCount = 0
  private listeners = new Set<NativeInputStreamEvents>()

  constructor(ctx: AudioContext, deviceId: string = '') {
    this.ctx = ctx
    this.deviceId = deviceId
  }

  /** Channel count of the currently-active native stream. `0` before
   *  `start()` resolves or after `stop()`. Consumers that split per-
   *  channel (MultiInput) read this to size their splitter. */
  getChannelCount(): number {
    return this.currentChannelCount
  }

  subscribe(listener: NativeInputStreamEvents): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getStatus(): NativeInputStreamStatus {
    return this.status
  }

  /** The terminal node consumers should connect into the graph.
   *  Returns `null` before `start()` has resolved. */
  getOutputNode(): AudioNode | null {
    return this.node
  }

  /** Open the native stream + build the worklet. Safe to call
   *  redundantly — short-circuits when already active. The device
   *  binding comes from the instance's constructor-fixed
   *  `deviceId`; to target a different device, ask
   *  `ensureNativeInputStream(ctx, otherDeviceId)` for a new
   *  instance. */
  async start(): Promise<void> {
    if (!hasNativeAudioPlugin()) {
      throw new Error(
        'Native input streaming requires the BusswerksAudio plugin',
      )
    }
    if (this.status === 'active') {
      return
    }
    this.setStatus('starting')
    try {
      await this.ensureWorkletModule()
      // Call native first so we know the actual channel count before
      // we size the worklet node. `outputChannelCount` is fixed at
      // construction, so we re-build the node on every start to
      // accommodate device changes (laptop mic is mono, interface
      // is multi-channel, hotplug may change it mid-session).
      const handle = await nativeStartInputStream({ deviceId: this.deviceId })
      const channelCount = Math.max(1, handle.channelCount)
      this.currentChannelCount = channelCount
      // Re-build the node whenever the channel count changes. Cheap
      // — AudioWorkletNodes don't hold wasm state of their own.
      this.disposeNode()
      this.node = new AudioWorkletNode(this.ctx, 'native-input-processor', {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        // Emit the FULL native channel count. For single-channel
        // devices the worklet duplicates mono across any extra
        // requested channels (see `process()`); for multi-channel
        // devices each output channel maps 1:1 to a source channel,
        // which is what MultiInput's splitter expects in P4.
        outputChannelCount: [channelCount],
      })
      this.node.port.onmessage = event => {
        const data = event.data as NodeStatusMessage | null
        if (data?.type === 'status') {
          for (const l of this.listeners) l.onFill?.(data.fill)
        }
      }
      // Subscribe AFTER constructing the node so the first frame
      // event has somewhere to go. A few ms of native frames may
      // arrive before the subscription lands; they're emitted via
      // notifyListeners (buffered by Capacitor) so the subscription
      // picks them up without losing data.
      this.unsubscribe = subscribeInputFrames(frames => {
        // Filter by this instance's device key so two concurrent
        // streams (internal mic + external interface) don't each
        // consume the other's frames. Legacy payloads without a
        // deviceId field (pre-P10 builds) fall through to the
        // default-device instance.
        const payloadKey = frames.deviceId ?? ''
        if (payloadKey !== this.deviceId) return
        this.handleNativeFrames(frames)
      })
      // Reconnect to the previously-chosen destination if any (the
      // node was torn down + rebuilt above).
      if (this.connectedTo) {
        this.node.connect(this.connectedTo)
      }
      this.setStatus('active')
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Native input stream failed'
      this.setStatus('error')
      for (const l of this.listeners) l.onError?.(message)
      this.teardownSubscriptionOnly()
      throw err
    }
  }

  /** Route the worklet node's output to `destination`. Pass `null`
   *  to leave the node disconnected (status stays `active` so audio
   *  buffers continue to drain, preventing overflow). */
  connectTo(destination: AudioNode | null): void {
    if (!this.node) {
      this.connectedTo = destination
      return
    }
    if (this.connectedTo === destination) return
    if (this.connectedTo) {
      try {
        this.node.disconnect(this.connectedTo)
      } catch {
        /* already disconnected */
      }
    }
    this.connectedTo = destination
    if (destination) {
      this.node.connect(destination)
    }
  }

  /** Stop the native stream, disconnect the node, unsubscribe from
   *  the event bridge. Idempotent. Currently stops the native
   *  engine unconditionally — with multiple concurrent instances
   *  on Catalyst, a stop on one may also halt the shared engine.
   *  Multi-engine native support (future) will key the stop by
   *  deviceId and only tear the per-device engine down. */
  stop(): void {
    this.teardownSubscriptionOnly()
    void nativeStopInputStream().catch(() => {
      /* plugin may already be in the stopped state */
    })
    this.disposeNode()
    this.connectedTo = null
    this.currentChannelCount = 0
    this.setStatus('idle')
  }

  /** Tear down the worklet node so a subsequent `start()` can build
   *  a fresh one with a (possibly different) outputChannelCount. */
  private disposeNode(): void {
    if (!this.node) return
    try {
      this.node.disconnect()
    } catch {
      /* already disconnected */
    }
    this.node.port.onmessage = null
    this.node = null
  }

  private teardownSubscriptionOnly(): void {
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
    }
  }

  /** Decode an `inputFrames` event into a `Float32Array` and post
   *  to the worklet. Uses the fastest available base64 decoder for
   *  the runtime — the manual char-code loop is the slow path we
   *  only take on older engines.
   *
   *  Perf ladder:
   *    1. `Uint8Array.fromBase64(str)` — intrinsic path, ~8× faster
   *       than the manual loop. Safari 16.4+, Chrome 124+.
   *    2. `Uint8Array.from(atob(str), ch => ch.charCodeAt(0))` —
   *       engine-optimised iterator, ~3× faster than the manual
   *       loop. Broadly supported.
   *    3. Manual loop (legacy fallback) — what we had before. */
  private handleNativeFrames(frames: NativeInputFrames): void {
    if (!this.node) return
    const bytes = decodeBase64Bytes(frames.data)
    // `Float32Array` needs a word-aligned buffer; both decoder
    // paths allocate a fresh `Uint8Array`, so the buffer IS word-
    // aligned and the typed-view never traps on misaligned reads.
    const floats = new Float32Array(
      bytes.buffer,
      bytes.byteOffset,
      Math.floor(bytes.byteLength / 4),
    )
    // Transfer the Float32Array's backing ArrayBuffer to the
    // worklet — zero-copy hand-off. We only own the buffer for
    // this tick so transferring it is safe; the main-thread
    // reference is abandoned after postMessage.
    this.node.port.postMessage(
      {
        type: 'frames',
        data: floats,
        channelCount: frames.channelCount,
        sampleRate: frames.sampleRate,
      },
      [floats.buffer],
    )
  }

  private async ensureWorkletModule(): Promise<void> {
    if (moduleRegistered.has(this.ctx)) return
    await this.ctx.audioWorklet.addModule(nativeInputProcessorUrl)
    moduleRegistered.add(this.ctx)
  }

  private setStatus(next: NativeInputStreamStatus): void {
    if (this.status === next) return
    this.status = next
    for (const l of this.listeners) l.onStatus?.(next)
  }
}

// ── Per-device registry ─────────────────────────────────────────
//
// P10 replaces the old single-instance singleton with a
// `Map<deviceId, NativeInputStream>` so MultiInput can open
// concurrent streams per device on Catalyst. The JS side is
// fully multi-device-aware; the native side currently still
// serialises engine usage (last start() wins), but the keyed
// architecture means we can flip on proper multi-engine native
// support without touching any of the consumer call sites.

const streamsByCtx = new WeakMap<AudioContext, Map<string, NativeInputStream>>()

/** Normalise the user-supplied device id to the key shape the
 *  native side uses in `inputFrames.deviceId` (empty string for
 *  "system default"). Keeps the registry stable whether callers
 *  pass `''`, `undefined`, or `'default'`. */
function normaliseDeviceKey(deviceId: string | undefined): string {
  if (!deviceId) return ''
  if (deviceId === 'default') return ''
  return deviceId
}

/**
 * Resolve the `NativeInputStream` instance for a specific device.
 * Creates fresh per-(ctx, deviceId) instances on first request and
 * caches them for the AudioContext's lifetime.
 *
 * Legacy callers that want "the default input" pass no deviceId.
 */
export function ensureNativeInputStream(
  ctx: AudioContext,
  deviceId: string = '',
): NativeInputStream {
  let byDevice = streamsByCtx.get(ctx)
  if (!byDevice) {
    byDevice = new Map()
    streamsByCtx.set(ctx, byDevice)
  }
  const key = normaliseDeviceKey(deviceId)
  const existing = byDevice.get(key)
  if (existing) return existing
  const created = new NativeInputStream(ctx, key)
  byDevice.set(key, created)
  return created
}

/** Read the current per-(ctx, deviceId) stream without creating
 *  one. Returns `null` when nothing is registered yet. Useful for
 *  UI code that wants to read status without triggering a lazy
 *  instantiation side-effect. */
export function getNativeInputStream(
  ctx: AudioContext,
  deviceId: string = '',
): NativeInputStream | null {
  const byDevice = streamsByCtx.get(ctx)
  if (!byDevice) return null
  return byDevice.get(normaliseDeviceKey(deviceId)) ?? null
}
